/**
 * Exercise library per focus — ported from the design kit's plan-data.jsx LIB.
 * The builder's auto-pick takes the top 6 for a day's focus; the mid-session
 * sheet searches across all of it. Targets here are the seed defaults; the
 * builder prefers the member's last logged actual when it has one.
 */

import type { TargetMap, WorkoutType } from './planShared';

export interface LibraryExercise {
  name: string;
  workoutType: WorkoutType;
  target: TargetMap;
}

const E = (name: string, workoutType: WorkoutType, target: TargetMap): LibraryExercise => ({ name, workoutType, target });

const BASE: Record<string, LibraryExercise[]> = {
  Arms: [
    E('Barbell curl', 'strength', { sets: 4, weight: 30, reps: 10 }),
    E('Cable pushdown', 'strength', { sets: 3, weight: 25, reps: 12 }),
    E('Hammer curl', 'strength', { sets: 3, weight: 14, reps: 12 }),
    E('Overhead extension', 'strength', { sets: 3, weight: 20, reps: 12 }),
    E('Preacher curl', 'strength', { sets: 3, weight: 20, reps: 10 }),
    E('Dead hang', 'hold', { hold: 45 }),
  ],
  Chest: [
    E('Bench press', 'strength', { sets: 5, weight: 70, reps: 5 }),
    E('Incline DB press', 'strength', { sets: 4, weight: 24, reps: 10 }),
    E('Cable fly', 'strength', { sets: 3, weight: 15, reps: 14 }),
    E('Dips', 'body', { sets: 3, reps: 10 }),
    E('Push-up finisher', 'body', { sets: 2, reps: 20 }),
    E('Pec deck', 'strength', { sets: 3, weight: 35, reps: 12 }),
  ],
  Legs: [
    E('Back squat', 'strength', { sets: 5, weight: 100, reps: 5 }),
    E('Romanian deadlift', 'strength', { sets: 4, weight: 80, reps: 8 }),
    E('Leg press', 'strength', { sets: 3, weight: 140, reps: 12 }),
    E('Walking lunge', 'body', { sets: 3, reps: 20 }),
    E('Leg curl', 'strength', { sets: 3, weight: 35, reps: 12 }),
    E('Calf raise', 'strength', { sets: 4, weight: 60, reps: 15 }),
  ],
  Back: [
    E('Deadlift', 'strength', { sets: 5, weight: 140, reps: 3 }),
    E('Pull-up', 'body', { sets: 4, reps: 8 }),
    E('Barbell row', 'strength', { sets: 4, weight: 60, reps: 10 }),
    E('Lat pulldown', 'strength', { sets: 3, weight: 50, reps: 12 }),
    E('Seated row', 'strength', { sets: 3, weight: 45, reps: 12 }),
    E('Face pull', 'strength', { sets: 3, weight: 20, reps: 15 }),
  ],
  Shoulders: [
    E('Overhead press', 'strength', { sets: 5, weight: 45, reps: 5 }),
    E('Lateral raise', 'strength', { sets: 4, weight: 10, reps: 15 }),
    E('Rear delt fly', 'strength', { sets: 3, weight: 9, reps: 15 }),
    E('Arnold press', 'strength', { sets: 3, weight: 16, reps: 10 }),
    E('Upright row', 'strength', { sets: 3, weight: 30, reps: 12 }),
    E('Shrug', 'strength', { sets: 3, weight: 70, reps: 12 }),
  ],
  Core: [
    E('Plank', 'hold', { hold: 90 }),
    E('Hanging leg raise', 'body', { sets: 4, reps: 12 }),
    E('Cable crunch', 'strength', { sets: 3, weight: 30, reps: 15 }),
    E('Ab wheel', 'body', { sets: 3, reps: 10 }),
    E('Dead bug', 'body', { sets: 3, reps: 12 }),
    E('Treadmill flush', 'run', { dist: 2, time: 12 }),
  ],
  Engine: [
    E('Row', 'erg', { dist: 2, time: 8 }),
    E('Assault bike', 'erg', { cal: 100 }),
    E('Sled push', 'circuit', { rounds: 6 }),
    E('Burpee ladder', 'circuit', { rounds: 5, time: 10 }),
    E('Ski erg', 'erg', { dist: 1, time: 5 }),
    E('Farmer carry', 'circuit', { rounds: 4 }),
  ],
  'Run + stations': [
    E('1 km repeats', 'run', { dist: 5, time: 30 }),
    E('Ski erg', 'erg', { dist: 1, time: 5 }),
    E('Sled push', 'circuit', { rounds: 4 }),
    E('Burpee broad jump', 'body', { reps: 20 }),
    E('Wall ball', 'strength', { weight: 6, reps: 50 }),
    E('Row', 'erg', { dist: 1, time: 4 }),
  ],
  'Race strength': [
    E('Back squat', 'strength', { sets: 4, weight: 90, reps: 6 }),
    E('Deadlift', 'strength', { sets: 4, weight: 120, reps: 5 }),
    E('Walking lunge', 'body', { sets: 3, reps: 20 }),
    E('Sled pull', 'circuit', { rounds: 4 }),
    E('Farmer carry', 'circuit', { weight: 24, rounds: 3 }),
    E('Sandbag squat', 'strength', { sets: 3, weight: 20, reps: 12 }),
  ],
  // The race-sim day runs the interval block and then all eight stations in
  // race order — nine items, so the builder seeds more than the usual six.
  'Race sim': [
    E('Interval runs 8×1 km', 'run', { dist: 8, time: 48 }),
    E('Ski erg', 'erg', { dist: 1, time: 5 }),
    E('Sled push', 'circuit', { rounds: 4 }),
    E('Sled pull', 'circuit', { rounds: 4 }),
    E('Burpee broad jump', 'body', { reps: 40 }),
    E('Row', 'erg', { dist: 1, time: 4 }),
    E('Farmer carry', 'circuit', { weight: 24, rounds: 2 }),
    E('Sandbag lunge', 'strength', { weight: 20, reps: 50 }),
    E('Wall ball', 'strength', { weight: 6, reps: 100 }),
  ],
};

/** Focus aliases from the recipes, exactly as the prototype maps them. */
export const EXERCISE_LIBRARY: Record<string, LibraryExercise[]> = {
  ...BASE,
  Push: BASE.Chest,
  Pull: BASE.Back,
  Upper: BASE.Chest,
  Lower: BASE.Legs,
  'Full body': BASE.Legs,
  Strength: BASE.Back,
  'Long effort': BASE.Engine,
  'Easy run': BASE.Core,
  Intervals: BASE.Engine,
  'Long run': BASE.Engine,
  'Erg engine': BASE.Engine,
};

/** The pools a member can browse by name (aliases stay out of the picker). */
export const FOCUS_GROUPS = Object.keys(BASE);

/**
 * Top picks for a focus (empty for unknown/rest focuses). The race-sim day is
 * the one focus that seeds its full nine-station list rather than the top six.
 */
export function picksForFocus(focus: string, count = 6): LibraryExercise[] {
  return (EXERCISE_LIBRARY[focus] ?? []).slice(0, focus === 'Race sim' ? 9 : count);
}

/** Flat, de-duplicated list for the mid-session search sheet. */
export function allExercises(): LibraryExercise[] {
  const seen = new Set<string>();
  const out: LibraryExercise[] = [];
  for (const list of Object.values(BASE)) {
    for (const e of list) {
      if (seen.has(e.name)) continue;
      seen.add(e.name);
      out.push(e);
    }
  }
  return out;
}
